import { defineStore } from "pinia";
import Swal from "sweetalert2";
import { registerResource, refreshResourceDebounced } from "~/services/repos/versionSync";
import { isEmployeePinEnabled } from "~/constants/packagePlan";


//Schema 

// {
//     "Row": 0,
//     "HideCashier": false,
//     "Pin2": null,
//     "Role": null,
//     "Pin": 12,
//     "Id": 423,
//     "CustomerId": 2,
//     "Name": "RICE",
//     "Editable": true
// }




// A shipped build wrote the discount permission as "pos_discount" instead of
// "pos_set_discount", so both spellings exist in shops' saved Role strings and
// both must authorize the discount action.
const ROLE_ALIASES: Record<string, string[]> = {
  pos_set_discount: ["pos_set_discount", "pos_discount"],
};

export const roleTokens = (roleStr?: string | null): string[] =>
  (roleStr || "").split(",").map((s) => s.trim()).filter(Boolean);

export const employeeHasRole = (employee: any, cmdPermission: string): boolean => {
  if (!employee) return false;
  if (employee.Editable) return true; // "All"
  const accepted = ROLE_ALIASES[cmdPermission] || [cmdPermission];
  const tokens = roleTokens(employee.Role);
  return accepted.some((t) => tokens.includes(t));
};

// this is for EMPLYEES PIN
export const usePosEmployeesStore = defineStore("employees", {
  state: () => ({
    employees: [],
    employeesSetting: [],
    employee: null,
    lastEmployeeName: null as string | null,
    // Name of the employee whose PIN approved the last checkPermission call;
    // null when the current employee already had the role (no approval needed).
    lastApprovedBy: null as string | null,
    loaded: false,
    reloadNewData : false
  }),

  getters: {
    getCurrentEmployee: (state) => {
      return {
        name: state.employee?.Name || "unknown",
        id: state.employee?.Id || "unknown",
        ...state.employee
      };
    },

    // Single source of truth for whether the PIN lock screen is active.
    // Active only when the package includes the PIN feature (>=890) AND the shop
    // has actually added staff. Package 590, or any shop with an empty employee
    // list, runs unlocked as the default "Admin".
    lockEnabled: (state) => isEmployeePinEnabled() && state.employees.length > 0,
  },

  actions: {
    getAdminEmployee() {
      return {
        Name: "admin",
        Id: "admin",
        Editable: true
      };
    },

    init() {
      const applyEmployees = (raw: any) => {
        const employees = Array.isArray(raw) ? raw : [];
        this.employeesSetting = employees;
        this.employees = employees;
        if (employees.length === 0) {
          this.employee = this.getAdminEmployee();
        }
      };
      registerResource({
        resource: "employees",
        refetch: () => useDataservice().employees.fetch(),
        applyToStore: applyEmployees,
      });
    },

    finishLoad() {
      this.loaded = true;
      // When the lock is not active (package 590, or any shop with no staff),
      // run unlocked as the default Admin so the POS is usable immediately.
      if (!this.lockEnabled && !this.employee) {
        this.employee = this.getAdminEmployee();
      }
    },

    /** Refresh + bump hash after a successful employee mutation. Debounced
     *  so rapid edits collapse into one refetch + Firestore bump. */
    refresh() {
      refreshResourceDebounced('employees');
    },
 
    enterPin(pin: string) {
      if (pin == 'pos') {
        this.employee = this.getAdminEmployee();
        this.lastEmployeeName = this.employee?.Name || null;
        return this.employee
      }

      if (this.employees.length == 0) {
        this.employee = this.getAdminEmployee();
        this.lastEmployeeName = this.employee?.Name || null;
        return this.employee
      }

      const employee = this.employees.find((e) => e.Pin === parseInt(pin));
      if (employee) {
        this.employee = employee;
        this.lastEmployeeName = employee?.Name || null;
        return this.employee
      } else {
        // alert("Employee not found");
        return null
      }
    },


    // `opts.containerClass` raises the PIN prompt above a host modal. SweetAlert2's
    // container sits at z-index 1060, so a call site rendered inside a higher stack
    // (e.g. the CUSTOMIZE ITEM modal at z-[2000]) would otherwise show the prompt
    // BEHIND its own overlay. Callers that don't pass it keep the default z-index.
    async checkPermission(cmdPermission: string, actionLabel?: string, opts?: { containerClass?: string }) {

      this.lastApprovedBy = null;

      return new Promise<boolean>(async (resolve) => {

        const employee = this.getCurrentEmployee;

        if (employeeHasRole(employee, cmdPermission)) {
          return resolve(true);
        }

        const { $i18n } = useNuxtApp() as any;
        // Prefer the translated action name; call sites pass an English
        // actionLabel which is only the fallback.
        const action =
          $i18n?.te?.(`lockScreen.permission.actions.${cmdPermission}`)
            ? $i18n.t(`lockScreen.permission.actions.${cmdPermission}`)
            : (actionLabel || cmdPermission);

        const result = await Swal.fire({
          icon: 'warning',
          title: $i18n?.t('lockScreen.permission.title', { action }) ?? `ใส่ PIN เพื่อ${action}`,
          input: 'password',
          inputLabel: $i18n?.t('lockScreen.permission.inputLabel') ?? 'ใส่ PIN ที่ได้รับสิทธิ์',
          inputPlaceholder: $i18n?.t('lockScreen.permission.inputPlaceholder') ?? 'ใส่ PIN',
          inputAttributes: {
            autocapitalize: 'off',
            autocorrect: 'off',
            autocomplete: 'off',
          },
          showCancelButton: true,
          confirmButtonText: $i18n?.t('lockScreen.permission.submit') ?? 'ตกลง',
          showLoaderOnConfirm: true,
          ...(opts?.containerClass ? { customClass: { container: opts.containerClass } } : {}),
        });

        if (!result.isConfirmed) {
          return resolve(false);
        }

        const found = this.employees.find((e) => e.Pin === parseInt(result.value));
        const hasPermission = found && employeeHasRole(found, cmdPermission);

        if (!hasPermission) {
          await Swal.fire({
            icon: 'error',
            title: $i18n?.t('lockScreen.permission.deniedTitle') ?? 'รหัสนี้ไม่มีสิทธิ์',
            text: found
              ? ($i18n?.t('lockScreen.permission.deniedText', { name: found.Name, action })
                  ?? `PIN ของ ${found.Name} ไม่ได้รับสิทธิ์สำหรับ${action}`)
              : ($i18n?.t('lockScreen.permission.notFound') ?? 'ไม่พบ PIN นี้ในระบบ'),
            ...(opts?.containerClass ? { customClass: { container: opts.containerClass } } : {}),
          });
          return resolve(false);
        }

        this.lastApprovedBy = found.Name || null;
        resolve(true);
      });


    },

    lockScreen() {
      this.employee = null;
    },
  },
});
